import KPIChangeDisplay from '@components/Display/KPIChangeDisplay/KPIChangeDisplay';
import { BASELINE_TYPE, TARGET_WASTE_FIELDS, WARNINGS, WARNINGS_TYPE } from '@constants/index';
import { Divider, Stack } from '@esgian/esgianui';
import { getPercentDiff, getValue, roundToSixDecimals } from '@helpers';
import { useDisplayUnit, useBaselineQueries } from '@hooks';
import { getDisplayUnit } from '@store/features';
import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { getMainDurationDays } from '@store/features/filters/MainPageSlice/MainPageSlice';

function AnalyticsComparisonKpis({ mainData, baselineData, loading }) {
  const displayUnit = useSelector(getDisplayUnit);
  const { valueWithUnit, getKpiDisplayText } = useDisplayUnit();
  const mainDurationDays = useSelector(getMainDurationDays);
  const { focusAreas } = useBaselineQueries();

  const mainFocusAreas = focusAreas[0].data;
  const baseFocusAreas = focusAreas[1].data;
  const mergedFocusAreas = mainFocusAreas?.length && baseFocusAreas?.length ? [...new Set([...mainFocusAreas, ...baseFocusAreas])] : [];

  const getSumDataTimeWell = (main, baseline, type) => {
    let valueKey = `${displayUnit.toLowerCase()}Saving`;
    let mainData = main.filter((item) => item.type === type) || [];
    let baseData = baseline.filter((item) => item.type === type) || [];

    let mainTot = 0;
    let baseTot = 0;
    mainData.forEach((value) => {
      mainTot += getValue(value[valueKey]);
    });

    baseData.forEach((value) => {
      baseTot += getValue(value[valueKey]);
    });

    return {
      ...valueWithUnit(mainTot - baseTot, displayUnit, true),
      percent: getPercentDiff(baseTot, mainTot)
    };
  };
  const getSumDataTarget = (main, baseline, type) => {
    let valueKey = `${displayUnit.toLowerCase()}Saving`;
    let mainData = main.filter((item) => item.type === type) || [];

    let baseTot = baseline?.[`${TARGET_WASTE_FIELDS[type]}${displayUnit}Waste`];
    let mainTot = 0;
    mainData.forEach((value) => {
      mainTot += getValue(value[valueKey]);
    });

    if (baseline.daily) {
      baseTot = roundToSixDecimals(baseTot * mainDurationDays);
      // Apply same logic to the main values from the api as for the target baseline.
      // When we save a daily target baseline we store it with six decimals. i.e both values needs to be equally "inaccurate" to make sure we don't get
      // trailing comma differance.
      mainTot = roundToSixDecimals(mainTot / mainDurationDays);
      mainTot = roundToSixDecimals(mainTot * mainDurationDays);
    } else {
      mainTot = roundToSixDecimals(mainTot);
    }

    return {
      ...valueWithUnit(mainTot - baseTot, displayUnit, true),
      percent: getPercentDiff(baseTot, mainTot)
    };
  };

  const kpis = useMemo(() => {
    if (!mainData || !baselineData) return {};

    // If main rig has no analytics data, show dashes
    if (Array.isArray(mainData) && mainData.length === 0) {
      const nullKpi = { value: null, title: null, percent: null };
      return {
        total: nullKpi,
        dw: nullKpi,
        hpu: nullKpi,
        mpIdle: nullKpi,
        tooManyEng: nullKpi,
        tooManyAgitators: nullKpi
      };
    }

    let valueKey = `${displayUnit.toLowerCase()}Saving`;
    let mainSum = 0;
    let baseSum = 0;
    let hpu, dw, tooManyEng, mpIdle, tooManyAgitators;

    if (baselineData.baselineTypeId === BASELINE_TYPE.TARGET) {
      let mainGrouped = {};
      // We need to treat the main data in the same order as on the create baseline modal.
      // if we don't group and then round the values then we'll get a trailing decimal point
      mainData.forEach((item) => {
        if (!mainGrouped[item.type]) {
          mainGrouped[item.type] = 0;
        }
        mainGrouped[item.type] += getValue(item[valueKey]);
      });
      Object.values(mainGrouped).forEach((value) => {
        // Apply same logic to the main values from the api as for the target baseline.
        // When we save a daily target baseline we store it with six decimals. i.e both values needs to be equally "inaccurate" to make sure we don't get
        // trailing comma differance.
        let val = roundToSixDecimals(value / mainDurationDays);
        val = roundToSixDecimals(val * mainDurationDays);
        mainSum += val;
      });

      Object.values(TARGET_WASTE_FIELDS).forEach((warning) => {
        baseSum += baselineData[`${warning}${displayUnit}Waste`] ?? 0;
      });
      if (baselineData.daily) {
        baseSum *= mainDurationDays;
      }

      tooManyEng = getSumDataTarget(mainData, baselineData, WARNINGS_TYPE.TooManyEng);
      mpIdle = getSumDataTarget(mainData, baselineData, WARNINGS_TYPE.MP_AUX_Idle);
      hpu = getSumDataTarget(mainData, baselineData, WARNINGS_TYPE.TooManyHPU);
      dw = getSumDataTarget(mainData, baselineData, WARNINGS_TYPE.DW_AUX_Idle);
      tooManyAgitators = getSumDataTarget(mainData, baselineData, WARNINGS_TYPE.TooManyAgitators);
    } else {
      mainData.forEach((item) => {
        mainSum += getValue(item[valueKey]);
      });
      tooManyEng = getSumDataTimeWell(mainData, baselineData, WARNINGS_TYPE.TooManyEng);
      mpIdle = getSumDataTimeWell(mainData, baselineData, WARNINGS_TYPE.MP_AUX_Idle);
      hpu = getSumDataTimeWell(mainData, baselineData, WARNINGS_TYPE.TooManyHPU);
      dw = getSumDataTimeWell(mainData, baselineData, WARNINGS_TYPE.DW_AUX_Idle);
      tooManyAgitators = getSumDataTimeWell(mainData, baselineData, WARNINGS_TYPE.TooManyAgitators);
      baselineData.forEach((item) => {
        baseSum += getValue(item[valueKey]);
      });
    }

    return {
      total: {
        ...valueWithUnit(mainSum - baseSum, displayUnit, true),
        percent: getPercentDiff(baseSum, mainSum)
      },
      dw: {
        ...dw
      },
      hpu: {
        ...hpu
      },
      mpIdle: {
        ...mpIdle
      },
      tooManyEng: {
        ...tooManyEng
      },
      tooManyAgitators: {
        ...tooManyAgitators
      }
    };
  }, [mainData, baselineData, displayUnit]);
  return (
    <Stack
      direction={'row'}
      justifyContent={'space-evenly'}
      divider={<Divider sx={{ height: 'inherit' }} orientation={'vertical'} />}>
      <KPIChangeDisplay
        loading={loading}
        percentChange={kpis.total?.percent}
        value={kpis.total?.value ?? null}
        unit={kpis.total?.title}
        title={'Total savings potential'}
        tooltipEnterDelayMs={500}
        tooltipText={'This figure indicates the total saving potential difference between the selected rig and the baseline at the end of the comparison period.'}
      />
      {mergedFocusAreas.includes(WARNINGS_TYPE.TooManyEng) && <KPIChangeDisplay
        loading={loading}
        percentChange={kpis.tooManyEng?.percent}
        value={kpis.tooManyEng?.value ?? null}
        unit={kpis.tooManyEng?.title}
        title={`${WARNINGS.TooManyEng}`}
        tooltipEnterDelayMs={500}
        tooltipText={'This figure indicates the engines` saving potential difference between the selected rig and the baseline at the end of the comparison period. Note that, for engines, there is no energy saving potential, only fuel and emissions.'}
      />}
      {mergedFocusAreas.includes(WARNINGS_TYPE.MP_AUX_Idle) && <KPIChangeDisplay
        loading={loading}
        percentChange={kpis.mpIdle?.percent}
        value={kpis.mpIdle?.value ?? null}
        unit={kpis.mpIdle?.title}
        title={`${WARNINGS.MP_AUX_Idle}`}
        tooltipEnterDelayMs={500}
        tooltipText={'This figure indicates the mud pump` saving potential difference between the selected rig and the baseline at the end of the comparison period.'}
      />}
      {mergedFocusAreas.includes(WARNINGS_TYPE.TooManyHPU) && <KPIChangeDisplay
        loading={loading}
        percentChange={kpis.hpu?.percent}
        value={kpis.hpu?.value ?? null}
        unit={kpis.hpu?.title}
        title={`${WARNINGS.TooManyHPU}`}
        tooltipEnterDelayMs={500}
        tooltipText={'This figure indicates the HPU` saving potential difference between the selected rig and the baseline at the end of the comparison period.'}
      />}
      {mergedFocusAreas.includes(WARNINGS_TYPE.DW_AUX_Idle) && <KPIChangeDisplay
        loading={loading}
        percentChange={kpis.dw?.percent}
        value={kpis.dw?.value ?? null}
        unit={kpis.dw?.title}
        title={`${WARNINGS.DW_AUX_Idle}`}
        tooltipEnterDelayMs={500}
        tooltipText={'This figure indicates the drawworks` saving potential difference between the selected rig and the baseline at the end of the comparison period.'}
      />}
      {mergedFocusAreas.includes(WARNINGS_TYPE.TooManyAgitators) && <KPIChangeDisplay
        loading={loading}
        percentChange={kpis.tooManyAgitators?.percent}
        value={kpis.tooManyAgitators?.value ?? null}
        unit={kpis.tooManyAgitators?.title}
        title={`${WARNINGS.TooManyAgitators}`}
        tooltipEnterDelayMs={500}
        tooltipText={'This figure indicates the agitators` saving potential difference between the selected rig and the baseline at the end of the comparison period. Note that, for agitators, there is no energy saving potential, only fuel and emissions.'}
      />}
    </Stack>
  );
}

AnalyticsComparisonKpis.propTypes = {
  mainData: PropTypes.arrayOf(PropTypes.object),
  baselineData: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.object), PropTypes.object]),
  loading: PropTypes.bool
};

AnalyticsComparisonKpis.defaultProps = {
  mainData: [],
  baselineData: [],
  loading: false
};

export default AnalyticsComparisonKpis;
